import React, { useEffect, useRef, useState } from "react";
import SearchField from "../forms/search-field";
import { useDispatch } from "react-redux";
import { phoneListAction } from "../../redux/actions/general-action";
import { useNavigate } from "react-router-dom";
import { useFormikContext } from "formik";
import AddButton from "../buttons/button";
import {
  AlternativeIcon,
  PastBillsIcon,
  ProductDetailsIcon,
  StoriesIcon,
} from "../../assets/icons/invoice-header-icons";
import OutstandingBalanceInfo from "../helper-components/outstanding-bal-information";
import PersonIcon from "../../assets/icons/person-icon";
import ProDetRightSlidePanel from "./pro-det-right-side-panel";
import BillingAlternativeItemPanel from "./billing-alternative-item-panel";
import BillingPastBillPanel from "./billing-past-bills-top-products";
import BillingOtherStores from "./billing-other-stores";
import { useHotkeys } from "react-hotkeys-hook";
import ItemsInputField from "../forms/items-input-field";
import {
  fetchBillNumberAction,
  invoiceDraftCreateAction,
  invoiceEditAction,
} from "../../redux/actions/invoices-action";
import LoadingOverlay from "../helper-components/overlay-loading";
import AddressSidebar from "../common-components/address-bar";
import { MapPin } from "lucide-react";
import SearchCustomerField from "../forms/search-customer-field";
import successToast from "../helper-components/success-toast";
import errorToast from "../helper-components/error-toast";
import moment from "moment";
import { handleShowErrorToast } from "../helper-components/helper-function";

const BillingHeaderSection = ({
  name,
  type,
  searchRef,
  selectedItem,
  qtyRef,
  disabled = false,
  page = "create",
  is_auto_batch,
  setBatchModalOpen,
  paymentPanel = false,
  prefixName = "sot",
  storeId,
}) => {
  const [loading, setLoading] = useState(false);
  const alternativesRef = useRef(null);
  const otherStoresRef = useRef(null);
  const proDetRef = useRef(null);
  const pastRef = useRef(null);
  const addressRef = useRef(null);

  useHotkeys("a", () => {
    if (alternativesRef.current && !paymentPanel) {
      alternativesRef.current.click();
    }
  });
  useHotkeys("o", () => {
    if (otherStoresRef.current) {
      otherStoresRef.current.click();
    }
  });
  useHotkeys("d", () => {
    if (proDetRef.current) {
      proDetRef.current.click();
    }
  });
  useHotkeys("p", () => {
    if (pastRef.current && !paymentPanel) {
      pastRef.current.click();
    }
  });
  useHotkeys("l", () => {
    if (addressRef.current && values.phone) {
      addressRef.current.click();
    }
  });
  const [PastBillPanel, setPastBillPanel] = useState(false);
  const [addressPanel, setAddressPanel] = useState(false);

  const [proDetSlidePanel, setProDetSlidePanel] = useState(false);
  const [altItemPanel, setAltItemPanel] = useState(false);
  const [otherStoresPanel, setOtherStoresPanel] = useState(false);
  const [customerId, setCustomerId] = useState("");

  const [localData, setLocalData] = useState([]);
  const [initialValueProcessed, setInitialValueProcessed] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setFieldValue, setFieldError, values, formValuesRef, validateForm } =
    useFormikContext();

  useEffect(() => {
    const handleInitialValue = async () => {
      // Check if there's an initial phone value and we haven't processed it yet
      if (values.phone?.value && !initialValueProcessed) {
        const phoneValue = values.phone.label || values.phone.value;

        // Extract phone number from label if it's in format "Name,PhoneNumber"
        const phoneNumber = phoneValue.includes(",")
          ? phoneValue.split(",")[1]?.trim()
          : phoneValue;

        if (phoneNumber) {
          const onSuccess = (data) => {
            // Create the same options format as in loadOptions
            const options = [
              ...data.data.map((item) => ({
                value: item.cmr_id,
                label: item.cmr_first_name
                  ? item.cmr_first_name + "," + item.cmr_phone_number
                  : item.cmr_phone_number,
              })),
              {
                value: { text: "new_customer", number: phoneNumber },
                label: <span className="text-primary">+ New Customer</span>,
              },
            ];

            setLocalData(data.data);

            // Find the customer data that matches the initial phone value
            const customerData = data.data.find(
              (item) =>
                item.cmr_id === values.phone.value ||
                item.cmr_phone_number === phoneNumber ||
                item.cmr_phone_number === phoneValue
            );

            if (customerData) {
              // Apply the same logic as in onChange
              setCustomerId(customerData.cmr_id);
              setFieldValue("cmr_id", customerData.cmr_id);
              setFieldValue("cmr_first_name", customerData.cmr_first_name);
              setFieldValue("cmr_last_name", customerData.cmr_last_name);
              setFieldValue(
                "cmr_acquired_source",
                customerData.cmr_acquired_source
              );
              setFieldValue(
                "cmr_credit_balance",
                customerData.cmr_credit_balance
              );
              setFieldError("phone", "");

              // If the current phone value doesn't match the expected format, update it
              const expectedOption = options.find(
                (opt) => opt.value === customerData.cmr_id
              );
              if (
                expectedOption &&
                values.phone.label !== expectedOption.label
              ) {
                setFieldValue("phone", expectedOption);
              }
            } else {
              // If no exact match found, but we have the phone number,
              // check if we need to create a "new customer" scenario
              const isNewCustomerScenario = !data.data.some(
                (item) => item.cmr_phone_number === phoneNumber
              );

              if (isNewCustomerScenario && phoneNumber) {
                // Handle as new customer case - similar to manual selection
                console.log("Initial value represents a new customer scenario");
              }
            }

            setInitialValueProcessed(true);
          };

          const onError = (error) => {
            console.error("Error loading initial customer data:", error);
            setInitialValueProcessed(true);
          };

          // Call the same API as in loadOptions
          dispatch(phoneListAction({ phone: phoneNumber }, onSuccess, onError));
        } else {
          setInitialValueProcessed(true);
        }
      } else if (!values.phone?.value) {
        // Reset the flag if phone value is cleared
        setInitialValueProcessed(false);
      }
    };

    handleInitialValue();
  }, [
    values.phone,
    initialValueProcessed,
    dispatch,
    setFieldValue,
    setFieldError,
  ]);

  const loadOptions = (inputValue) => {
    return new Promise((resolve) => {
      const onSuccess = (data) => {
        const options = [
          ...data.data.map((item) => ({
            value: item.cmr_id,
            label: item.cmr_first_name
              ? item.cmr_first_name + "," + item.cmr_phone_number
              : item.cmr_phone_number,
          })),
          {
            value: { text: "new_customer", number: inputValue },
            label: <span className="text-primary">+ New Customer</span>,
          },
        ];
        setLocalData(data.data);
        resolve(options);
      };

      const onError = (error) => {
        resolve([
          {
            value: { text: "new_customer", number: inputValue },
            label: <span className="text-primary">+ New Customer</span>,
          },
        ]);
      };

      if (inputValue?.length > 0) {
        dispatch(
          phoneListAction(
            {
              phone: inputValue,
            },
            onSuccess,
            onError
          )
        );
      } else {
        resolve([]);
      }
    });
  };

  function fetchBillNumber(backdate) {
    setLoading(true);
    dispatch(
      fetchBillNumberAction(
        {
          backdate: backdate,
          doc_type: prefixName === "sot" ? "billing" : "return",
        },
        onSuccess,
        onError
      )
    );
  }

  const onSuccess = (data) => {
    setFieldValue(`${prefixName}_custom_invoice_number`, data?.data || "");
    setLoading(false);
  };
  const onError = (data) => {
    setLoading(false);
  };
  // const condition = customerId !== "";
  // const divClass = condition ? "tab-container w-full h-min" : "tab-container";

  // const divStyle = condition ? { overflowY: "hidden", overflowX: "auto" } : {};
  const handleDraft = async () => {
    const formErrors = await validateForm();
    if (Object.keys(formErrors).length > 0) {
      return handleShowErrorToast(formErrors);
    }
    console.log(values, "fgfdgdfgfdgdfgfdgfdg");

    let actionType = invoiceDraftCreateAction;
    if (page === "edit") {
      actionType = invoiceEditAction;
    }
    const filteredData = values?.data;
    if (
      filteredData.some(
        (item) => parseFloat(item?.item_margin_percentage) < 0
      ) &&
      values?.submittedButton !== "draft"
    ) {
      errorToast("Margin is negative, please check");
    } else {
      if (values?.data?.length > 0) {
        setTimeout(() => {
          setLoading(true);
          dispatch(
            actionType(
              {
                orderData: {
                  is_draft_order:
                    values.submittedButton === "draft" ? true : false,
                  store_id: values?.store_id || storeId || "",
                  cmr_id: values?.cmr_id || "",
                  cmr_phone_number: parseInt(values?.phone?.label),
                  sot_total_gst: values?.sot_total_gst || 0,
                  sot_total_discount: values?.sot_total_discount || 0,
                  sot_payment_status:
                    values.submittedButton === "draft" ? "pending" : "paid",
                  sot_transaction_id: "",
                  sot_billing_address: "",
                  sot_remarks: values?.sot_remarks,
                  sot_sub_total: values?.sot_sub_total || 0,
                  sot_total_amount: values?.sot_total_amount || 0,
                  sot_round_off_value: values?.sot_round_off_value || 0,
                  doctor_name: values?.doctor_name?.value,
                  sot_is_invoice_dis_percentage:
                    values?.sot_is_invoice_dis_percentage,
                  sot_invoice_discount: values?.sot_invoice_discount,
                  sot_invoice_discount_percentage:
                    values?.sot_invoice_discount_percentage,
                  sot_prescription: values?.sot_prescription,
                  ...(values?.sot_id ? { sot_id: values?.sot_id } : {}),
                  sot_is_store_pick_up: values?.sot_is_store_pick_up,
                  sot_custom_invoice_date:
                    values?.sot_custom_invoice_date || null,
                  sot_custom_invoice_number:
                    values?.sot_custom_invoice_number || "",
                  sot_pay_to_address: values.sot_pay_to_address,
                  sot_ship_to_address: values.sot_ship_to_address,
                },
                paymentData: values?.paymentData?.map((pay) => ({
                  payment_mode_name: pay?.payment_mode_name,
                  payment_amount: pay?.payment_amount,
                  transaction_id: pay?.transaction_id || "",
                })),
                itemData: values?.data?.map((item, index) => ({
                  item_code: item?.item_code,
                  item_generic_name: item?.item_generic_name,
                  item_name: item?.item_name,
                  item_quantity: parseInt(item?.quantity),
                  item_pack_qty: item?.item_pack_qty || 0,
                  item_loose_qty: item?.item_loose_qty || 0,
                  item_free_quantity:
                    parseInt(item?.item_free_quantity ?? 0, 10) || 0,
                  item_uom: item?.item_uom,
                  item_batch_number: item?.item_batch_number,
                  item_exp_date: moment(item?.item_exp_date).format(
                    "YYYY-MM-DD"
                  ),
                  item_rack_location: item?.item_rack_location,
                  item_schedule: item?.item_schedule,
                  item_hsn: item?.item_hsn,
                  item_unit_price: parseFloat(item?.item_unit_price),
                  item_price_wiithout_tax: parseFloat(
                    item?.item_price_wiithout_tax
                  ),
                  item_discount_amount: parseFloat(item?.item_discount_amount),
                  item_discount_percentage:
                    parseFloat(item?.item_discount_percentage) || 0,
                  item_tax_amount: parseFloat(item?.item_tax_amount),
                  item_total_amount: parseFloat(
                    parseFloat(item.item_unit_price) * parseInt(item.quantity) -
                      parseFloat(item.item_discount_amount)
                  ).toFixed(2),
                  item_id: item?.item_id,
                  item_gst: parseFloat(item?.item_gst),
                  item_sgst: parseFloat(item?.item_sgst),
                  item_cgst: parseFloat(item?.item_cgst),
                  item_total_tax_percentage: parseFloat(
                    item?.item_total_tax_percentage
                  ),
                  item_pack_price: item?.item_pack_price || 0,
                  // item_manufacturer_id: item?.item_manufacturer_id ?? "",
                  // item_manufacturer_name: item?.item_manufacturer_name ?? "",
                })),
              },
              (data) => {
                setLoading(false);
                // resetForm();
                // setPaymentPanel(false);
                // if (values.submittedButton === "print") {
                //   handlea4Print(
                //     data?.data?.orderData?.[0]?.sot_id,
                //     "Billing",
                //     dispatch
                //   );

                //Needed for unwrap
                // handlePrint(data?.data, storeDetails);
                // }
                successToast(data?.message);
                setLoading(true);
                setTimeout(() => {
                  setLoading(false);
                }, 50);
                navigate(
                  `/edit-customer-admin/${values?.cmr_id}?backto=/edit-bill/${data?.data?.orderData?.[0]?.sot_id}`,
                  {
                    state: { tab: "Address" },
                  }
                );
                // navigate(
                //   `/add-doctor?doctor=${
                //     selectedOption?.value?.number?.toString() || ""
                //   }&draft=${data?.data?.orderData?.[0]?.sot_id}`
                // );

                formValuesRef.current = null; // Reset after handling
              },
              (data) => {
                errorToast(data?.data?.message);
              }
            )
          );
        }, 500); // Timeout set to 100ms
      }
    }
  };
  return (
    <div className="min-h-[8rem] flex items-center w-full ">
      {/* <div className="w-full overflow-x-auto overflow-y-visible tab-container"> */}
      <div className="justify-between bg-[var(--add-billing-head-bg)] border-radius h-[87px] flex items-center px-3 w-max min-w-full">
        <div className="flex gap-3 items-end">
          <div className="w-[12rem] z-30  h-fit">
            <SearchCustomerField
              disabled={disabled || page === "view"}
              maxlength={10}
              className="z-20"
              isValidNewOption={true}
              autoFocus={true}
              name={"phone"}
              placeholder="Enter Name/Phone"
              onCreateOption={() => {}}
              loadOptions={loadOptions}
              onChange={(selectedOption) => {
                if (
                  selectedOption === "new_customer" ||
                  selectedOption?.value?.text === "new_customer"
                ) {
                  navigate(
                    `/add-customer?phoneNumber=${
                      selectedOption?.value?.number?.toString().slice(0, 10) ||
                      ""
                    }`
                  );
                } else {
                  setFieldValue("phone", selectedOption);
                  const customerData = localData?.find(
                    (item) => item?.cmr_id === selectedOption?.value
                  );
                  setFieldValue("cmr_id", customerData?.cmr_id);
                  setCustomerId(customerData?.cmr_id);
                  setFieldValue("cmr_first_name", customerData?.cmr_first_name);
                  setFieldValue("cmr_last_name", customerData?.cmr_last_name);
                  setFieldValue(
                    "cmr_acquired_source",
                    customerData?.cmr_acquired_source
                  );
                  setFieldValue(
                    "cmr_credit_balance",
                    customerData?.cmr_credit_balance
                  );
                  setFieldError("phone", "");

                  if (searchRef && searchRef.current && selectedOption?.value) {
                    searchRef.current.focus();
                  }
                }
              }}
            />
          </div>

          {values?.cmr_first_name && (
            <OutstandingBalanceInfo
              transac="billing"
              badgeText={
                <span className="flex  gap-2">
                  <PersonIcon width="18px" height="18px" className="w-5 h-5" />
                  {values?.cmr_first_name}
                </span>
              }
            />
          )}

          <div className="min-w-[5rem]">
            <ItemsInputField
              labelClass="min-h-[1.2rem] text-[var(--search-input-label-color)] text-[length:var(--search-label-font-size)] font-[var(--search-label-font-weight)]"
              inputClass="min-h-[40px] px-2 border border-[var(--search-input-border)] focus:outline-none focus:border-[var(--search-input-focus)] bg-[var(--search-input-bg)] text-[var(--search-input-color)] text-[length:var(--search-input-font-size)] font-[var(--search-input-font-weight)] border-radius input-field-no-spin-buttons"
              max={new Date().toISOString().split("T")[0]}
              type="date"
              disabled={disabled}
              name={`${prefixName}_custom_invoice_date`}
              label="Bill Date"
              isLabelReq={true}
              placeholder="Created Date"
              onChange={(e) => {
                if (e?.target?.value) {
                  fetchBillNumber(e?.target?.value);
                } else {
                  setFieldValue(`${prefixName}_custom_invoice_number`, "");
                }
              }}
            />
          </div>
          <div className="min-w-[8rem]">
            <ItemsInputField
              labelClass="min-h-[1.2rem] text-[var(--search-input-label-color)] text-[length:var(--search-label-font-size)] font-[var(--search-label-font-weight)]"
              inputClass="min-h-[40px] px-2 border border-[var(--search-input-border)] focus:outline-none focus:border-[var(--search-input-focus)] bg-[var(--search-input-bg)] text-[var(--search-input-color)] text-[length:var(--search-input-font-size)] font-[var(--search-input-font-weight)] border-radius input-field-no-spin-buttons"
              type="text"
              disabled={disabled}
              name={`${prefixName}_custom_invoice_number`}
              label="Invoice Number"
              isLabelReq={true}
              placeholder="Custom Invoice Num"
            />
          </div>
          {type === "return" && (
            <div className="min-w-[20rem]">
              <ItemsInputField
                maxLength={30}
                placeholder="Enter Reason for return bill"
                isRequired={false}
                labelClass="min-h-[1.2rem] text-[var(--search-input-label-color)] text-[length:var(--search-label-font-size)] font-[var(--search-label-font-weight)]"
                inputClass="min-h-[40px] px-2 border border-[var(--search-input-border)] focus:outline-none focus:border-[var(--search-input-focus)] bg-[var(--search-input-bg)] text-[var(--search-input-color)] text-[length:var(--search-input-font-size)] font-[var(--search-input-font-weight)] border-radius input-field-no-spin-buttons"
                type="text"
                disabled={disabled}
                name={`icn_reason`}
                label="Reason"
                isLabelReq={true}
              />
            </div>
          )}
        </div>

        <div className="flex justify-end  ml-3  overflow-hidden">
          <div
            className=" flex flex-row items-center  gap-5  overflow-x-auto overflow-y-hidden tab-container"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {page !== "view" && (
              <div className="flex items-center gap-5">
                <AddButton
                  ref={alternativesRef}
                  onclkFn={() => {
                    setAltItemPanel(!altItemPanel);
                  }}
                  icon={<AlternativeIcon />}
                  text={
                    <>
                      Alternatives <span className="opacity-50">(A)</span>
                    </>
                  }
                  className={`flex items-center px-[12px] h-[44px] whitespace-nowrap rounded-[8px] gap-[8px] 
            bg-[var(--general-button-bg)] cursor-pointer 
            border border-[var(--general-button-border)] 
            hover:bg-[var(--general-button-bg)] ${
              !disabled
                ? "text-[var(--general-button-fg)]"
                : "text-[var(--general-button-disabled-fg)] opacity-60 pointer-events-none"
            }`}
                />
                <AddButton
                  ref={otherStoresRef}
                  onclkFn={() => {
                    setOtherStoresPanel(!otherStoresPanel);
                  }}
                  icon={<StoriesIcon />}
                  text={
                    <>
                      Other Stores <span className="opacity-50">(O)</span>
                    </>
                  }
                  className={`flex items-center px-[12px] h-[44px] whitespace-nowrap rounded-[8px] gap-[8px] 
            bg-[var(--general-button-bg)] cursor-pointer 
            border border-[var(--general-button-border)] 
            hover:bg-[var(--general-button-bg)] 
            ${
              !disabled
                ? "text-[var(--general-button-fg)]"
                : "text-[var(--general-button-disabled-fg)] opacity-60 pointer-events-none"
            }`}
                />
                <AddButton
                  ref={proDetRef}
                  onclkFn={() => {
                    if (selectedItem?.itemid)
                      setProDetSlidePanel(!proDetSlidePanel);
                  }}
                  icon={<ProductDetailsIcon />}
                  text={
                    <>
                      Product Details <span className="opacity-50">(D)</span>
                    </>
                  }
                  className={`flex items-center px-[12px] h-[44px] whitespace-nowrap rounded-[8px] gap-[8px]
    bg-[var(--general-button-bg)] cursor-pointer 
    border border-[var(--general-button-border)] 
    hover:bg-[var(--general-button-bg)]
    
    ${
      selectedItem?.itemid
        ? "text-[var(--general-button-fg)]"
        : "text-[var(--general-button-disabled-fg)] opacity-60 pointer-events-none"
    }
    `}
                />

                <AddButton
                  ref={pastRef}
                  icon={<PastBillsIcon />}
                  text={
                    <>
                      Past Bills <span className="opacity-50">(P)</span>
                    </>
                  }
                  className={`flex items-center px-[12px] h-[44px] whitespace-nowrap rounded-[8px] gap-[8px] 
            bg-[var(--general-button-bg)] cursor-pointer 
            border border-[var(--general-button-border)] 
            hover:bg-[var(--general-button-bg)]
            
            ${
              !disabled && values?.phone
                ? "text-[var(--general-button-fg)]"
                : "text-[var(--general-button-disabled-fg)] opacity-60 pointer-events-none"
            }`}
                  onclkFn={() => {
                    setPastBillPanel(!PastBillPanel);
                  }}
                />
              </div>
            )}

            <AddButton
              ref={addressRef}
              icon={<MapPin size={13} />}
              text={
                <>
                  Address Details <span className="opacity-50">(L)</span>
                </>
              }
              className={`flex items-center px-[12px] h-[44px] whitespace-nowrap rounded-[8px] gap-[8px] 
            bg-[var(--general-button-bg)] cursor-pointer 
            border border-[var(--general-button-border)] 
            hover:bg-[var(--general-button-bg)]
            
              ${
                values?.phone
                  ? "text-[var(--general-button-fg)]"
                  : "text-[var(--general-button-disabled-fg)] opacity-60 pointer-events-none"
              }`}
              onclkFn={() => {
                setAddressPanel(!addressPanel);
              }}
            />
          </div>
        </div>
      </div>

      {console.log("000", values?.cmr_id)}

      {page !== "view" && (
        <>
          <ProDetRightSlidePanel
            selectedItem={selectedItem}
            open={proDetSlidePanel}
            setOpen={setProDetSlidePanel}
          />
          <BillingPastBillPanel
            open={PastBillPanel}
            setOpen={setPastBillPanel}
          />

          <BillingAlternativeItemPanel
            setBatchModalOpen={setBatchModalOpen}
            is_auto_batch={is_auto_batch}
            qtyRef={qtyRef}
            open={altItemPanel}
            setOpen={setAltItemPanel}
            selectedItem={selectedItem}
          />
          <BillingOtherStores
            selectedItem={selectedItem}
            open={otherStoresPanel}
            setOpen={setOtherStoresPanel}
          />
        </>
      )}
      <AddressSidebar
        handleDraft={handleDraft}
        type={type}
        open={addressPanel}
        setOpen={setAddressPanel}
        id={
          type === "return"
            ? values.cmr_id
            : page === "add"
            ? customerId
            : values.cmr_id
        }
        name={name}
        page={page}
        disabled={disabled}
      />
      <LoadingOverlay isLoading={loading} />
      {/* </div> */}
    </div>
    // </div>
  );
};

export default BillingHeaderSection;
